import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'

import Button from '../components/common/Button'
import TootiDialog from '../components/common/Dialog'
import ListRow from '../components/common/ListRow'
import ParrotMark from '../components/common/ParrotMark'
import Spinner from '../components/common/Spinner'
import { BadgeGrid, FreezeCard } from '../components/shop/GemCards'
import TeamCard from '../components/social/TeamCard'
import UserPhoto from '../components/social/UserPhoto'
import { logoutDialog } from '../lib/alerts'
import { faDigits } from '../lib/numerals'
import { levelLine, practisedDays, profileName } from '../lib/profile'
import { profileCompletion } from '../lib/profileForm'
import { weekdayInitials } from '../lib/progress'
import { TONE, avatarById, gemsOn } from '../lib/shop'
import { planLabel } from '../lib/subscriptions'
import { appVersionLine } from '../lib/version'
import { classifyFailure } from '../services/api'
import { myStats } from '../services/lessons'
import { listNotifications } from '../services/notifications'
import { shopWallet } from '../services/shop'
import { subscriptionStatus } from '../services/subscriptions'
import { myTeam } from '../services/teams'
import { useAuthStore } from '../store/authStore'
import { useToastStore } from '../store/toastStore'
import { ProgressSection } from './Progress'

function FlameOutline() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" className="shrink-0 text-accent" aria-hidden="true">
      <path d="M12 3c0 3-4 4-4 8a4 4 0 0 0 8 0c0-1.5-1-3-1-3s2 1.5 2 4a5.5 5.5 0 0 1-11 0c0-5 6-6 6-9z" />
    </svg>
  )
}

function StarGlyph() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" className="shrink-0 fill-accent" aria-hidden="true">
      <path d="M12 2.6l2.5 6 6.5.5-4.9 4.2 1.5 6.3L12 16.2 6.4 19.6l1.5-6.3L3 9.1l6.5-.5z" />
    </svg>
  )
}

function CheckGlyph() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  )
}

function StarBadgeGlyph() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 2.6l2.5 6 6.5.5-4.9 4.2 1.5 6.3L12 16.2 6.4 19.6l1.5-6.3L3 9.1l6.5-.5z" fill="currentColor" />
    </svg>
  )
}

function GearGlyph() {
  return (
    <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1A1.6 1.6 0 0 0 9 19.4a1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 4.6 9a1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9a1.6 1.6 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9a1.6 1.6 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1z" />
    </svg>
  )
}

/**
 * The design's streak card: the streak and its record, the points row,
 * and a gold tick on every day of the week with a minute on it. The
 * sub-line counts the attempts rather than quoting the design's «هر دقیقه
 * = ۱۰ امتیاز»: the XP rule is the backend's provisional one, not this
 * client's to state.
 */
export function StreakCard({ days, longest, points, attempts, week }) {
  const { t } = useTranslation('profile')
  const ticks = practisedDays(week)
  const weekdays = weekdayInitials()
  return (
    <section className="space-y-[15px] rounded-[20px] bg-linear-to-br from-hero-from to-hero-to p-[18px] text-white shadow-[0_6px_16px_rgba(43,90,150,0.22)]">
      <div className="flex items-center gap-[11px]">
        <FlameOutline />
        <div className="flex flex-col gap-0.5">
          <span className="text-[23px] font-bold leading-tight">{t('streakCard.daysStreak', { count: faDigits(days) })}</span>
          <span className="text-[11.5px] text-white/75">{t('streakCard.longestRecord', { count: faDigits(longest) })}</span>
        </div>
      </div>
      <div className="flex items-center gap-3 rounded-[14px] bg-white/15 px-[13px] py-3">
        <StarGlyph />
        <div className="flex flex-1 flex-col gap-0.5">
          <span className="text-base font-bold">
            {faDigits(points)} <span className="text-[11px] font-semibold text-white/75">{t('streakCard.totalPoints')}</span>
          </span>
          <span className="text-[10.5px] text-white/75">{t('streakCard.attemptsSent', { count: faDigits(attempts) })}</span>
        </div>
      </div>
      <div className="flex justify-between gap-1.5">
        {ticks.map((on, index) => (
          <div key={weekdays[index]} className="flex flex-1 flex-col items-center gap-[7px]">
            <span data-practised={on} className={`grid size-7 place-items-center rounded-full ${on ? 'bg-accent text-on-accent' : 'bg-white/20 text-transparent'}`}>
              {on && <CheckGlyph />}
            </span>
            <span className={`text-[10px] ${on ? 'font-semibold text-white' : 'text-white/70'}`}>{weekdays[index]}</span>
          </div>
        ))}
      </div>
    </section>
  )
}

function StatTile({ value, label }) {
  return (
    <div className="flex flex-col items-center gap-1.5 card-soft rounded-tile px-2.5 py-3.5">
      <span className="text-[21px] font-bold text-ink">{value}</span>
      <span className="text-center text-[10.5px] text-ink-2">{label}</span>
    </div>
  )
}

/** «طوطی+ — همهٔ درس‌ها و تحلیل نامحدود»: the accent card that opens the paywall. */
function UpsellCard() {
  const { t } = useTranslation('profile')
  return (
    <Link to="/paywall" className="flex items-center gap-3 rounded-[18px] border-[1.5px] border-accent bg-accent-tint/60 p-3.5 transition hover:bg-accent-tint">
      <span className="grid size-11 shrink-0 place-items-center rounded-full bg-accent text-on-accent">
        <StarBadgeGlyph />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm font-bold text-accent-ink">{t('upsell.title')}</span>
        <span className="text-[11.5px] text-accent-ink">{t('upsell.subtitle')}</span>
      </span>
    </Link>
  )
}

/**
 * The profile tab (design page 13, `profile`; P-profile of the go-live
 * plan): who the learner is, their streak, three totals, the progress
 * section (the week's chart and report, moved in from `/progress` by
 * V3-w), and the row list that is the door to everything that is not a
 * tab. The streak, the week and the counts are `me/stats`; the avatar and
 * the coins are the wallet (B5); the unread count is the inbox (B4); the
 * subscription is B7; the team is V4 (`teams_mine`, shown only once teams
 * are switched on) — each read settles on its own.
 */
export default function Profile() {
  const { t } = useTranslation('profile')
  const user = useAuthStore((s) => s.user)
  const signOut = useAuthStore((s) => s.signOut)
  const showToast = useToastStore((s) => s.show)
  const location = useLocation()
  const [stats, setStats] = useState(null)
  const [error, setError] = useState(null)
  const [reload, setReload] = useState(0)
  const [wallet, setWallet] = useState(null)
  const [unread, setUnread] = useState(0)
  const [subscription, setSubscription] = useState(null)
  const [team, setTeam] = useState(null)
  const [askLogout, setAskLogout] = useState(false)

  // «پروفایل ذخیره شد.» arrives from the edit form as route state, once, and is said by the app's toast.
  const toast = location.state?.toast ?? null
  useEffect(() => {
    if (toast) showToast(toast)
  }, [toast, showToast])

  // Coming to the page re-reads the profile (the XP tile; throttled in the store), as the Android app does.
  useEffect(() => {
    void useAuthStore.getState().refreshProfile()
  }, [])

  useEffect(() => {
    let cancelled = false
    setError(null)
    myStats()
      .then((data) => {
        if (!cancelled) setStats(data)
      })
      .catch((e) => {
        if (!cancelled) setError(e)
      })
    shopWallet()
      .then((data) => {
        if (!cancelled) setWallet(data)
      })
      .catch(() => {})
    listNotifications()
      .then((data) => {
        if (!cancelled) setUnread(data.unreadCount ?? 0)
      })
      .catch(() => {})
    subscriptionStatus()
      .then((data) => {
        if (!cancelled) setSubscription(data)
      })
      .catch(() => {})
    myTeam()
      .then((data) => {
        if (!cancelled) setTeam(data)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [reload])

  const completion = profileCompletion(user?.fullName ?? '', user?.displayName ?? '')
  const avatar = avatarById(wallet?.avatar)
  const tone = TONE[avatar.tone] ?? TONE.brand

  return (
    <div className="mx-auto max-w-md space-y-3.5">
      <header className="flex items-center gap-[13px]">
        {/* V5-w: the learner's own photo once they have one (`me_profile.photoUrl`), else the shop avatar as before. */}
        <UserPhoto photoUrl={user?.photoUrl} size={58}>
          <span className={`grid size-[58px] shrink-0 place-items-center rounded-full border-[2.5px] ${tone.disc} ${tone.ring}`}>
            <ParrotMark size={34} />
          </span>
        </UserPhoto>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h1 className="truncate text-[19px] font-bold text-ink">{profileName(user)}</h1>
          <p className="text-[12.5px] text-ink-2">{levelLine(user?.level)}</p>
        </div>
        <Link to="/settings" aria-label={t('rows.settings')} className="grid size-11 shrink-0 place-items-center rounded-full text-ink-2 transition hover:bg-surface-2">
          <GearGlyph />
        </Link>
      </header>

      {error && !stats ? (
        <div className="space-y-3">
          <div role="status" className={`rounded-xl p-3 text-sm ${classifyFailure(error) === 'offline' ? 'bg-warn-tint text-warn-ink' : 'bg-bad-tint text-bad-ink'}`}>
            {classifyFailure(error) === 'offline' ? t('errors.offline') : t('errors.server')}
          </div>
          <Button variant="secondary" className="w-full" onClick={() => setReload((n) => n + 1)}>
            {t('errors.retry')}
          </Button>
        </div>
      ) : !stats ? (
        <Spinner label={t('loadingLabel')} />
      ) : (
        <>
          <StreakCard days={stats.streakCurrent ?? 0} longest={stats.streakLongest ?? 0} points={user?.xp ?? 0} attempts={stats.attemptCount ?? 0} week={stats.weekMinutes} />
          <div className="grid grid-cols-3 gap-2">
            <StatTile value={faDigits(stats.attemptCount ?? 0)} label={t('stats.attempts')} />
            <StatTile value={stats.attemptCount > 0 ? faDigits(Math.round(stats.averageScore ?? 0)) : '—'} label={t('stats.averageScore')} />
            <StatTile value={faDigits(stats.totalMinutes ?? 0)} label={t('stats.minutes')} />
          </div>
          <ProgressSection stats={stats} />
        </>
      )}

      <TeamCard answer={team} />

      {/* V6-w: freezes x/max and badges — nothing at all while the server's gem economy is off. */}
      <FreezeCard wallet={wallet} />
      <BadgeGrid wallet={wallet} />

      <UpsellCard />

      <div className="space-y-2">
        <ListRow label={t('rows.completion')} value={t('rows.completionValue', { count: faDigits(completion) })} to="/profile/edit" />
        <ListRow label={t('rows.notifications')} value={unread > 0 ? t('rows.unreadValue', { count: faDigits(unread) }) : ''} to="/notifications" />
        <ListRow label={t('rows.settings')} to="/settings" />
        <ListRow label={t('rows.archive')} value={stats ? t('rows.archiveValue', { count: faDigits(stats.attemptCount ?? 0) }) : ''} to="/archive" />
        <ListRow label={t('rows.shop')} value={wallet ? (gemsOn(wallet) ? t('rows.shopGemsValue', { count: faDigits(wallet.gems ?? 0) }) : t('rows.shopValue', { count: faDigits(wallet.coins ?? 0) })) : ''} to="/shop" />
        <ListRow label={t('rows.subscription')} value={subscription ? planLabel(subscription) : ''} to="/paywall" />
        <ListRow label={t('rows.logout')} onClick={() => setAskLogout(true)} destructive />
      </div>

      <p className="pt-0.5 text-center text-[11px] text-ink-3">{appVersionLine()}</p>

      {askLogout && (
        <TootiDialog
          spec={logoutDialog()}
          onConfirm={() => {
            setAskLogout(false)
            signOut()
          }}
          onDismiss={() => setAskLogout(false)}
        />
      )}
    </div>
  )
}
